/**
 * VM CPU Configuration
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';

export interface CpuConfig {
  model: string;
  flags: string[];
  threadsPerCpu: number;
}

// Module state
let currentCpuModel = 'host';
let currentCpuFlags: string[] = [];
let currentThreadsPerCpu = 1;
let cpuConfigTouched = false;

export function getCurrentCpuConfig(): CpuConfig | null {
  if (!cpuConfigTouched) return null;
  return {
    model: currentCpuModel,
    flags: currentCpuFlags,
    threadsPerCpu: currentThreadsPerCpu,
  };
}

export function resetCpuState(): void {
  currentCpuModel = 'host';
  currentCpuFlags = [];
  currentThreadsPerCpu = 1;
  cpuConfigTouched = false;
}

/**
 * Open CPU options configuration dialog
 */
export async function openCpuOptionsDialog(vmId: number | null = null): Promise<void> {
  cpuConfigTouched = true;
  const [options, cpuModels, cpuFlags] = await Promise.all([
    apiCall(api.getOptions(), 'Failed to Load Options'),
    apiCall(api.getCpuModels(), 'Failed to Load CPU Models'),
    apiCall(api.getCpuFlags(), 'Failed to Load CPU Flags'),
  ]);
  if (!options) return;

  const rawModels = cpuModels ?? options.cpu_models ?? {};
  const rawFlags = cpuFlags ?? options.cpu_flags ?? {};

  // Backend returns arrays; normalize to Record<string, string> so
  // each value is used as both the key and the display label.
  const toRecord = (v: unknown): Record<string, string> => {
    if (Array.isArray(v)) {
      const r: Record<string, string> = {};
      for (const item of v) r[String(item)] = String(item);
      return r;
    }
    return v as Record<string, string>;
  };
  const models = toRecord(rawModels);
  const flags = toRecord(rawFlags);

  if (vmId !== null) {
    try {
      const runtimeInfo = await api.getVmStatus(vmId);
      const info = runtimeInfo as unknown as Record<string, unknown>;
      currentCpuModel = (info.cpu_model as string) || 'host';
      currentCpuFlags = (info.cpu_flags as string[]) || [];
      currentThreadsPerCpu = (info.threads_per_cpu as number) || 1;
    } catch {
      currentCpuModel = 'host';
      currentCpuFlags = ['+topoext'];
      currentThreadsPerCpu = 1;
    }
  } else {
    currentCpuModel = 'host';
    currentCpuFlags = ['+topoext'];
    currentThreadsPerCpu = 1;
  }

  let modelOptions = '';
  for (const [value, label] of Object.entries(models)) {
    const selected = currentCpuModel === value ? 'selected' : '';
    modelOptions += `<option value="${escapeAttr(value)}" ${selected}>${escapeHtml(String(label))}</option>`;
  }

  let flagsHtml = '';
  for (const [flag, desc] of Object.entries(flags)) {
    const checked = currentCpuFlags.includes(flag) ? 'checked' : '';
    flagsHtml += `<label style="display:block;margin:3px 0;" title="${escapeAttr(String(desc))}">
      <input type="checkbox" class="cpu-flag" value="${escapeAttr(flag)}" ${checked}> ${escapeHtml(flag)}
      <small style="color:#666"> - ${escapeHtml(String(desc))}</small>
    </label>`;
  }

  const form = `
    <label>Threads per Core:</label>
    <select id="threads-per-cpu">
      <option value="1" ${currentThreadsPerCpu === 1 ? 'selected' : ''}>1 (No SMT)</option>
      <option value="2" ${currentThreadsPerCpu === 2 ? 'selected' : ''}>2 (SMT enabled)</option>
    </select><br><br>
    <label>CPU Model:</label>
    <select id="cpu-model">${modelOptions}</select><br><br>
    <label>CPU Flags:</label>
    <div style="max-height:200px;overflow-y:auto;border:1px solid #ccc;padding:5px;">
      ${flagsHtml}
    </div>
  `;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>CPU Configuration</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'CPU Options',
      width: 500,
      autoOpen: true,
      buttons: {
        Save: function () {
          currentCpuModel = $('#cpu-model').val() as string;
          currentThreadsPerCpu = parseInt($('#threads-per-cpu').val() as string);
          currentCpuFlags = [];
          $('.cpu-flag:checked').each(function () {
            currentCpuFlags.push($(this).val() as string);
          });
          $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vmModifyCpuOptions = openCpuOptionsDialog;
}
